import { Popover, Transition } from '@headlessui/react';
import { Fragment, useRef } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { IoTimeOutline } from 'react-icons/io5';

import cn from 'app/utils/ClassName';

import Button from '../Button';

import { TimeFilterInputField } from './TimeFilterInputField';

interface Props {
  name: string;
  label?: string;
  placeholder?: string;
  containerClassName?: string;
  labelClassName?: string;
  isDisable?: boolean;
  onChangeCallback?: () => void;
}

export const TimeField = ({
  name,
  label,
  placeholder,
  containerClassName,
  labelClassName,
  isDisable = false,
  onChangeCallback,
}: Props) => {
  const { trigger } = useFormContext();
  const { fieldState } = useController({ name });
  const time = useWatch({ name });
  const minuteRef = useRef<HTMLInputElement>(null);

  return (
    <div
      className={`relative flex flex-col gap-[8px] ${containerClassName}`}
      id={name}
    >
      <div className="flex flex-wrap items-end justify-between">
        {label ? (
          <label
            htmlFor={name}
            className={cn(
              'relative block text-[14px] font-semibold leading-[16.94px] text-text-primary',
              labelClassName,
            )}
          >
            {label}
          </label>
        ) : (
          <div />
        )}
      </div>
      <Popover className="relative">
        <Popover.Button
          type="button"
          variant="text"
          color="primary"
          onClick={() => null}
          containerStyle={cn(
            'flex flex-col gap-[8px] w-full hover:bg-transparent',
            isDisable ? 'cursor-not-allowed' : '',
          )}
          as={Button}
          disabled={isDisable}
        >
          <div className={cn(
            'relative flex h-[46px] w-full rounded-[6px] border-[1.24px] border-border-brand bg-white px-[16px] py-[12px]',
            isDisable ? 'cursor-not-allowed !bg-bg-disabled' : '',
          )}
          >
            <div
              className={cn(
                'w-full min-w-[188px] pr-[22px] text-left text-[16px] leading-[19.36px] tracking-[0.01em] text-text-primary focus:border-border-brand',
                time?.hour && time?.minute
                  ? 'text-text-primary'
                  : 'text-text-light',
              )}
            >
              {time?.hour?.length === 2 && time?.minute?.length === 2
                ? `${time.hour}:${time.minute}`
                : placeholder || label}
            </div>
            <IoTimeOutline className="size-[20px] text-text-light" />
          </div>
          {fieldState.error?.message || fieldState.error?.root?.message ? (
            <p className="text-left text-[12px] font-normal leading-[14.32px] tracking-[0.01em] text-brand-attention">
              {fieldState.error?.message || fieldState.error?.root?.message}
            </p>
          ) : (
            <div className="h-[14.32px]" />
          )}
        </Popover.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <Popover.Panel
            className="absolute left-0 z-10 mt-[-14px] flex flex-col border-0 bg-transparent pb-[60px]"
            onBlur={() => {
              trigger(name);
            }}
          >
            <div className="flex w-[300px] items-center gap-[10px] overflow-hidden rounded-md bg-white px-[16px] shadow-lg ring-1 ring-black/5">
              <TimeFilterInputField
                name={name}
                type="hour"
                nextInputRef={minuteRef}
                onChangeCallback={onChangeCallback}
              />
              <div className="text-[40px]">:</div>
              <TimeFilterInputField
                name={name}
                type="minute"
                inputRef={minuteRef}
                onChangeCallback={onChangeCallback}
              />
            </div>
          </Popover.Panel>
        </Transition>
      </Popover>
    </div>
  );
};
